import React, { useMemo } from 'react';

import { alpha, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

export interface SteppedProgressBarProps {
  className?: string;
  currentStep: number;
  dataFd?: string;
  isWarning?: boolean;
  totalSteps: number;
  variant?: 'default' | 'slim';
}

const useStyles = makeStyles((theme: Theme) => ({
  firstStepPointerContainer: {
    left: `-${theme.spacing(1.1)}`,
  },
  progressBar: {
    display: 'inline-flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    padding: theme.spacing(1.5, 0, 0.5, 0),
    width: '100%',

    '& span:first-child': {
      marginLeft: 0,
    },

    '& span:last-child': {
      marginRight: 0,
    },
  },

  step: {
    backgroundColor: alpha(theme.palette.common.black, 0.2),
    display: 'inline-flex',
    flexGrow: 1,
    height: '6px',
    margin: theme.spacing(0, 0.25),
    position: 'relative',
  },

  stepActive: {
    backgroundColor: theme.palette.success.main,
  },

  stepAnimation: {
    animation: '$stepAnimated 2800ms linear infinite',
    background: `linear-gradient(45deg, ${alpha(
      theme.palette.success.main,
      0,
    )} 10%, ${alpha(theme.palette.success.main, 0.5)} 50%, ${alpha(
      theme.palette.success.main,
      0,
    )} 90%)`,
    bottom: 0,
    position: 'absolute',
    top: 0,
    width: 80,
  },

  '@keyframes stepAnimated': {
    '0%': { left: -80 },
    '35%': { left: '100%' },
    '100%': { left: '100%' },
  },

  stepAnimationContainer: {
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },

  stepAnimationWarning: {
    background: `linear-gradient(45deg, ${alpha(
      theme.palette.warning.main,
      0,
    )} 10%, ${alpha(theme.palette.warning.main, 0.5)} 50%, ${alpha(
      theme.palette.warning.main,
      0,
    )} 90%)`,
  },

  stepPointerCircle: {
    animation: '$stepPointerCircle 1400ms infinite',
    backgroundColor: theme.palette.success.main,
    borderRadius: '50%',
    bottom: 0,
    height: '40%',
    left: 0,
    margin: 'auto',
    position: 'absolute',
    right: 0,
    top: 0,
    width: '40%',
  },

  '@keyframes stepPointerCircle': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.25)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },

  stepPointerContainer: {
    display: 'flex',
    height: '25px',
    position: 'absolute',
    right: `-${theme.spacing(1.25)}`,
    top: `-${theme.spacing(1.175)}`,
    width: '25px',
  },

  stepPointerContainerSlim: {
    display: 'flex',
    height: '18.5px',
    position: 'absolute',
    right: `-${theme.spacing(1.1)}`,
    top: `-${theme.spacing(0.9)}`,
    width: '18.5px',
  },

  stepPointerOuterCircle: {
    animation: '$stepPointerOuterCircle 1400ms infinite',
    backgroundColor: theme.palette.success.main,
    borderRadius: '50%',
    bottom: 0,
    height: '100%',
    left: 0,
    margin: 'auto',
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
  },

  '@keyframes stepPointerOuterCircle': {
    '0%': {
      transform: 'scale(.4)',
      opacity: 1,
    },
    '50%': {
      transform: 'scale(1)',
      opacity: 0.3,
    },
    '100%': {
      transform: 'scale(.4)',
      opacity: 1,
    },
  },

  stepSlim: {
    height: '4px',
  },
}));

export function SteppedProgressBar(
  props: SteppedProgressBarProps,
): JSX.Element {
  const {
    className = '',
    currentStep,
    dataFd = 'stepped-progress-bar',
    isWarning = false,
    totalSteps,
    variant = 'default',
  } = props;
  const classes = useStyles();

  const progressBarStyles = clsx(
    'fd-stepped-progress-bar',
    classes.progressBar,
    className !== '' && className,
  );

  const renderSteps = useMemo((): JSX.Element[] => {
    const steps = [] as JSX.Element[];

    for (let i = 0; i < totalSteps; i += 1) {
      const stepNumber = i + 1;
      const isActive = stepNumber <= currentStep;
      const isFirst = i === 0 && currentStep === 0;
      const showPointer = isFirst || currentStep === stepNumber;
      const isPending = stepNumber === currentStep + 1;

      const stepStyles = clsx(
        'fd-stepped-progress-bar-step',
        classes.step,
        variant === 'slim' && classes.stepSlim,
        isActive && classes.stepActive,
      );

      const stepAnimationStyles = clsx(
        'fd-stepped-progress-bar-step-animation',
        classes.stepAnimation,
        isWarning && classes.stepAnimationWarning,
      );

      const pointerStyles = clsx(
        'fd-stepped-progress-bar-pointer',
        variant === 'slim'
          ? classes.stepPointerContainerSlim
          : classes.stepPointerContainer,
        isFirst && classes.firstStepPointerContainer,
      );

      steps.push(
        <span className={stepStyles} key={`step-${stepNumber}`}>
          {isPending && (
            <div className={classes.stepAnimationContainer}>
              <div className={stepAnimationStyles} />
            </div>
          )}
          {showPointer && (
            <div className={pointerStyles}>
              <div className={classes.stepPointerCircle} />
              <div className={classes.stepPointerOuterCircle} />
            </div>
          )}
        </span>,
      );
    }

    return steps;
  }, [classes, currentStep, totalSteps, variant]);

  return (
    <div className={progressBarStyles} data-fd={dataFd}>
      {renderSteps}
    </div>
  );
}
